import { UpdatedAppointmentInformation } from "@/Appointments/Api/Dto/Responses/UpdatedAppointmentInformation";
import { PADisplaySettings } from "@/Appointments/Models/Settings/PADisplaySettings";
import { PAListDisplaySettings } from "@/Appointments/Models/Settings/PAListDisplaySettings";
import { IPADisplaySettingsModel } from "@/Appointments/Models/UI/IPADisplaySettingsModel";
import { IPAListModel } from "@/Appointments/Models/UI/IPAListModel";
import { PAListAction } from "@/Appointments/Stores/Actions/PAListAction";
import { PAListHandler } from "@/Appointments/Stores/Handlers/Local/PaList/PAListHandler";
import { PAListSettingsContainerHandler } from "@/Appointments/Stores/Handlers/Local/PaList/PAListSettingsContainerHandler";
import { PAListRealTimeNotificationHandler } from "@/Appointments/Stores/Handlers/PAListRealTimeNotificationHandler";
import { PAListResponseHandler } from "@/Appointments/Stores/Handlers/Server/PAListResponseHandler";
import { Appointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { PAListSortOrderType, SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { ICheckable } from "@/Common/Models/UI/Inputs/ICheckable";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseStore } from "@/Common/Stores/BaseStore";
import { RequestMapper, ServerResponseMapper } from "@/Common/Stores/Handlers/Server/RequestMapper";

let defaultInstance: PAListStore;

class PAListStore extends BaseStore {
    private Mapper: RequestMapper;
    private ServerMapper: ServerResponseMapper;

    private PAListHandler: PAListHandler;
    private PAListSettingsContainreHandler: PAListSettingsContainerHandler;
    private ResponseHandler: PAListResponseHandler;

    /**
    * Handles real-time PA list notifications from the server.
    */
    // @ts-ignore Suppresses 'noUnusedLocals' warning - the handler registers its own registered operations and needs to be initialized even if it's not used directly.
    private paListRealTimeNotificationHandler: PAListRealTimeNotificationHandler;

    public PAListModel: IPAListModel;
    public PADisplaySettingsModel: IPADisplaySettingsModel;

    public ColumnManagerModel: Immutable.List<ColumnConfigurationModel>;
    public TableColumnInfo: Immutable.List<MyleTableColumnOptions>;
    public TableRenderOptions: ITableRenderOptions;

    public PAListActions: PAListAction;

    public Helper: MyleHelper;

    public patientToCompleteWithRamqInfo: Appointment;

    constructor() {
        super();

        var self = this;

        this.Mapper = new RequestMapper();
        this.ServerMapper = new ServerResponseMapper();

        this.Helper = new MyleHelper();
        this.patientToCompleteWithRamqInfo = new Appointment();

        var initCols = this.DefineInitialColumnsForTable();
        var cols: MyleTableColumnOptions[] = [];

        // search helper to be used below
        var getColumnFromList = function(name: string, c: MyleTableColumnOptions[]): MyleTableColumnOptions {
            var q = c.filter(a => a.FieldName === name);
            if (q.length === 0) { return null; }
            else { return q[0]; }
        };

        // get display settings from cache
        var tbSettings: PAListDisplaySettings = cacheStore.UserSettings?.PAListDisplaySettings;

        if (tbSettings == null) {
            tbSettings = new PAListDisplaySettings();
            tbSettings.OrderedColumns = [];
        }

        if (tbSettings.OrderedColumns.length > 0) {
            for (let c of tbSettings.OrderedColumns) {
                var ci = getColumnFromList(c.ColumnName, initCols);

                if (ci != null) {
                    ci.IsHidden = c.IsHidden;
                    if (c.WidthRatio > 0) {
                        ci.WidthPercentage = c.WidthRatio;
                    }
                    else {
                        if (ci.WidthPixels == null) {
                            ci.WidthPercentage = ci.WidthPercentageDefault;
                        }
                    }
                    cols.push(ci);
                }
            }
        }
        else {
            cols = initCols.map(i => {
                i.WidthPercentage = i.WidthPercentageDefault;
                return i;
            });
        }

        var defaultPAListModel = {
            Whatever: "",
            TableData: [],
            ShowPAListDisplaySettings: false,
            CliniciansSearchText: "",
            ColumnOrderingPopupOpen: false,
            LastColumnOrdered: "StartTime",
            SaveSettingsChecked: false
        };

        var defaultPADisplaySettingsModel = {
            IsAllSitesChecked: true,
            IsAllEmployeesChecked: true,
            IsAllStatusChecked: false,
            Sites: [],
            EmployeesGlobalCache: [],
            EmployeesFiltered: [],
            AppointmentStatuses: cacheStore.GetCheckboxesByLookupTypeCode("SITEMGMT.APPOINTMENTS.STATE", CheckboxTarget.Myle_PAList_AppointmentStatusCheck, true)
        };

        // set default appointment state checks
        if (defaultPADisplaySettingsModel.AppointmentStatuses != null && defaultPADisplaySettingsModel.AppointmentStatuses.length > 0) {
            let confirmed = cacheStore.FindLookupByLookupCode("CONFIRMED");
            let nonconfirmed = cacheStore.FindLookupByLookupCode("UNCONFIRMED");
            let msgLeft = cacheStore.FindLookupByLookupCode("MESSAGE LEFT");
            defaultPADisplaySettingsModel.AppointmentStatuses.forEach((x: ICheckable) => {
                if (x.Id != confirmed.LookupID && x.Id != nonconfirmed.LookupID && x.Id != msgLeft.LookupID)
                    x.Checked = false;
            });
        }

        this.PAListModel = Immutable.fromJS(defaultPAListModel);
        this.PADisplaySettingsModel = Immutable.fromJS(defaultPADisplaySettingsModel);

        this.TableColumnInfo = Immutable.List(cols);
        this.ColumnManagerModel = Immutable.List([]);

        this.PAListActions = new PAListAction();
        this.PAListHandler = new PAListHandler();
        this.PAListSettingsContainreHandler = new PAListSettingsContainerHandler();
        this.ResponseHandler = new PAListResponseHandler(this.PAListActions);
        this.ServerMapper.AddInstance(this.ResponseHandler);

        this.Mapper.AddInstance(this.PAListHandler);
        this.Mapper.AddInstance(this.PAListSettingsContainreHandler);

        // real-time notification handler
        this.paListRealTimeNotificationHandler = new PAListRealTimeNotificationHandler(this);

        this.ClientDispatchToken = LocalEventDispatcher.register((request: LocalEvent) => self.Mapper.Map(request, self));
        this.ServerDispatchToken = ServerResponseDispatcher.register((response: ServerEvent) => { self.ServerMapper.Map(response, self); });
    }

    public LoadPAListDisplaySettings(paDisplaySettings: PADisplaySettings): void {
        let result = this.PADisplaySettingsModel
            .set("IsAllSitesChecked", paDisplaySettings.IsAllSitesChecked)
            .set("IsAllStatusChecked", paDisplaySettings.IsAllStatusChecked)
            .set("IsAllEmployeesChecked", paDisplaySettings.IsAllEmployeesChecked)
            .set("IsShowOnlySelectedSitesChecked", paDisplaySettings?.IsShowOnlySelectedSitesChecked ?? false)
            .set("AppointmentStatuses", this.Helper.ChangeCheckStateFromAllICheckable(this.PADisplaySettingsModel.get("AppointmentStatuses"), false))
            .set("Sites", this.Helper.ChangeCheckStateFromAllICheckable(this.PADisplaySettingsModel.get("Sites"), false))
            .set("EmployeesGlobalCache", this.Helper.ChangeCheckStateFromAllICheckable(this.PADisplaySettingsModel.get("EmployeesGlobalCache"), false));

        if (!paDisplaySettings.SiteIds?.any())
            result = result.set("Sites", this.Helper.ChangeCheckStateFromAllICheckable(this.PADisplaySettingsModel.get("Sites"), true));
        else
            result = result.set("Sites", this.Helper.FindUpdateICheckableItems(this.PADisplaySettingsModel.get("Sites"), paDisplaySettings.SiteIds, true));

        if (paDisplaySettings.EmployeeIds?.any())
            result = result.set("EmployeesGlobalCache", this.Helper.FindUpdateICheckableItems(this.PADisplaySettingsModel.get("EmployeesGlobalCache"), paDisplaySettings.EmployeeIds, true));
        else
            result = result.set("EmployeesGlobalCache", this.Helper.FindUpdateICheckableItems(this.PADisplaySettingsModel.get("EmployeesGlobalCache"), [sessionStore.individualContactId], true));

        if (!paDisplaySettings.StatusesIds?.any())
            result = result.set("AppointmentStatuses", this.Helper.ChangeCheckStateFromAllICheckable(this.PADisplaySettingsModel.get("AppointmentStatuses"), true));
        else
            result = result.set("AppointmentStatuses", this.Helper.FindUpdateICheckableItems(this.PADisplaySettingsModel.get("AppointmentStatuses"), paDisplaySettings.StatusesIds, true));

        result = result.set("EmployeesFiltered", this.getCliniciansBasedOnSitesAndQuery());
        this.PADisplaySettingsModel = result;
    }

    public static createSingleton(): PAListStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new PAListStore());
    }

    public loadClinicianUICache(): void {
        var list: EmployeeUIModel[] = [];
        for (let composite of cacheStore.EmployeeCache) {
            var employee = EmployeeUIModel.fromCompositeObject(composite);
            employee.Target = CheckboxTarget.Myle_PAList_Clinicians;
            list.push(employee);
        }
        this.PADisplaySettingsModel = this.PADisplaySettingsModel.set("EmployeesGlobalCache", Immutable.List(list));

        this.CheckTheClinicianTheMatchesTheCurrentSession();
    }

    public CheckTheClinicianTheMatchesTheCurrentSession(): void {
        var clinitians = this.PADisplaySettingsModel.get("EmployeesGlobalCache").toArray().map(cl => {
            cl.Checked = true;
            return cl;
        });

        this.PADisplaySettingsModel = this.PADisplaySettingsModel.set("EmployeesFiltered", Immutable.List(clinitians));
    }

    public getCliniciansBasedOnSitesAndQuery(): Immutable.List<EmployeeUIModel> {
        var cliniciansFromSites = this.getCliniciansFromSelectedSites();
        var filterString = $.trim(this.PAListModel.get("CliniciansSearchText"));
        if (filterString !== "") { cliniciansFromSites = this.filterCliniciansByName(filterString, cliniciansFromSites); }
        return cliniciansFromSites;
    }

    public getCliniciansFromSelectedSites(): Immutable.List<EmployeeUIModel> {
        var checkedSites = this.Helper.GetCheckedItemsFromICheckableList(this.PADisplaySettingsModel.get("Sites"));

        var allClinicians = this.PADisplaySettingsModel.get("EmployeesGlobalCache");

        var result: EmployeeUIModel[] = [];
        for (var i = 0; i < checkedSites.size; i++) {
            for (var j = 0; j < allClinicians.size; j++) {
                var clinician = allClinicians.get(j);
                var sites = clinician.Sites;
                var hasSite = sites.filter(item => item === checkedSites.get(i).Id).length > 0;
                if (hasSite) {
                    var toAdd = result.filter(i => i.Id === clinician.Id).length === 0;
                    if (toAdd) { result.push(clinician); } // prevent duplicates due to many-to-many relationship between clinicians and sites
                }
            }
        }
        return Immutable.List(result);
    }

    public filterCliniciansByName(str: string, collection: Immutable.List<EmployeeUIModel>): Immutable.List<EmployeeUIModel> {
        var search = new RegExp(str, "i");
        var filtered = collection.toArray().filter(item => search.test(item.Name));
        return Immutable.List(filtered);
    }

    public loadSitesFromCache(): void {
        var a: SiteUIModel[] = [];
        for (let composite of cacheStore.SiteCache) {
            var site = SiteUIModel.FromCompositeObject(composite);
            site.Checked = true;
            site.Target = CheckboxTarget.Myle_PAList_Sites;
            a.push(site);
        }
        this.PADisplaySettingsModel = this.PADisplaySettingsModel.set("Sites", Immutable.List(a));
    }

    /**
     * Update and reloads a single appointment in the PA list.
     * @param reloadedAppointment The appointment to be reloaded in the PA list.
     */
    public ReloadSingleAppointmentInPAList(reloadedAppointment: UpdatedAppointmentInformation, selectedAppointmentStatusIds: string[]): void {
        const currentAppointments = this.PAListModel.get("TableData").toArray();
        const listOfMatchesForReloadedAppointment = currentAppointments.filter(p => p.AppointmentId === reloadedAppointment.AppointmentId);

        const unmodifedAppointments = currentAppointments.filter(p => p.AppointmentId !== reloadedAppointment.AppointmentId);
        // This enables updates for appointments with multiple intervenors
        for (const appointment of listOfMatchesForReloadedAppointment) {
            const lookup = cacheStore.GetLookupById(reloadedAppointment.AppointmentStateLookupId);
            const lookupLocalizedName = cacheStore.GetLocalizedValueByLookupId(reloadedAppointment.AppointmentStateLookupId);

            appointment.AppointmentStateLookupId = reloadedAppointment.AppointmentStateLookupId;
            appointment.AppointmentStateLookupCode = lookup.LookupCode;
            appointment.AppointmentStateLookupName = lookupLocalizedName;
            appointment.StartTime = reloadedAppointment.StartTime;
            appointment.EndTime = reloadedAppointment.EndTime;
        }

        // Keep only the appointments that have a status that matches one checked in the user status filter
        const statusFilteredAppointments = listOfMatchesForReloadedAppointment.filter(
            (appointment: Appointment) => selectedAppointmentStatusIds.contains(appointment.AppointmentStateLookupId)
        );

        const updatedAppointments = unmodifedAppointments.concat(statusFilteredAppointments);
        this.PAListModel = this.PAListModel.set("TableData", Immutable.List(updatedAppointments));
    }

    public DefineInitialColumnsForTable(): MyleTableColumnOptions[] {
        var cols: MyleTableColumnOptions[] = [];

        var c_opt1 = new MyleTableColumnOptions();
        const defaultRowClickEnabled: boolean = true;

        c_opt1.HeaderText = cacheStore.GetString("HourTimeLabel");
        c_opt1.FieldName = "StartTime";
        c_opt1.RowClickEnabled = defaultRowClickEnabled;
        c_opt1.IsSortable = true;
        c_opt1.SortColumn = PAListSortOrderType.AppointmentTime;
        c_opt1.SortDirection = SortDirection.ASC;
        c_opt1.WidthPercentageDefault = 5;
        cols.push(c_opt1);

        var c_opt2 = new MyleTableColumnOptions();
        c_opt2.HeaderText = cacheStore.GetString("AttachAPatientLabel");
        c_opt2.FieldName = "PatientFirstName";
        c_opt2.RowClickEnabled = defaultRowClickEnabled;
        c_opt2.IsSortable = true;
        c_opt2.SortColumn = PAListSortOrderType.Patient;
        c_opt2.SortDirection = SortDirection.ASC;
        c_opt2.WidthPercentageDefault = 8;
        cols.push(c_opt2);

        var c_opt3 = new MyleTableColumnOptions();
        c_opt3.HeaderText = sessionStore.getDefaultHealthCardLabel();
        c_opt3.FieldName = "PatientNAM";
        c_opt3.RowClickEnabled = defaultRowClickEnabled;
        c_opt3.WidthPercentageDefault = 6;
        cols.push(c_opt3);

        var c_opt4 = new MyleTableColumnOptions();
        c_opt4.HeaderText = cacheStore.GetString("EmployeeLabel");
        c_opt4.FieldName = "EmployeeFullName";
        c_opt4.RowClickEnabled = defaultRowClickEnabled;
        c_opt4.WidthPercentageDefault = 8;
        cols.push(c_opt4);

        var c_opt5 = new MyleTableColumnOptions();
        c_opt5.HeaderText = cacheStore.GetString("FileNumberDLabel");
        c_opt5.FieldName = "PatientIdentifier1";
        c_opt5.RowClickEnabled = defaultRowClickEnabled;
        c_opt5.WidthPercentageDefault = 6;
        cols.push(c_opt5);

        var c_opt6 = new MyleTableColumnOptions();
        c_opt6.HeaderText = cacheStore.GetString("PhoneNumberLabel");
        c_opt6.FieldName = "PatientPhoneNumbersDisplay";
        c_opt6.RowClickEnabled = defaultRowClickEnabled;
        c_opt6.WidthPercentageDefault = 6;
        cols.push(c_opt6);

        var c_opt7 = new MyleTableColumnOptions();
        c_opt7.HeaderText = cacheStore.GetString("EmailLabel");
        c_opt7.FieldName = "PatientEmailsDisplay";
        c_opt7.RowClickEnabled = defaultRowClickEnabled;
        c_opt7.WidthPercentageDefault = 8;
        cols.push(c_opt7);

        var c_opt8 = new MyleTableColumnOptions();
        c_opt8.HeaderText = cacheStore.GetString("AddressLabel");
        c_opt8.FieldName = "PatientAddressesDisplay";
        c_opt8.RowClickEnabled = defaultRowClickEnabled;
        c_opt8.WidthPercentageDefault = 15;
        cols.push(c_opt8);

        const preferredPharmacyColumn = new MyleTableColumnOptions({
            HeaderText: cacheStore.GetString("PreferredPharmacyLabel"),
            FieldName: "PreferredPharmacy",
            RowClickEnabled: defaultRowClickEnabled,
            WidthPercentageDefault: 4
        });
        cols.push(preferredPharmacyColumn);

        var c_opt9 = new MyleTableColumnOptions();
        c_opt9.HeaderText = cacheStore.GetString("SubjectLabel");
        c_opt9.FieldName = "Subject";
        c_opt9.RowClickEnabled = defaultRowClickEnabled;
        c_opt9.WidthPercentageDefault = 8;
        cols.push(c_opt9);

        var c_opt10 = new MyleTableColumnOptions();
        c_opt10.HeaderText = cacheStore.GetString("SitesLabel");
        c_opt10.FieldName = "SiteName";
        c_opt10.RowClickEnabled = defaultRowClickEnabled;
        c_opt10.WidthPercentageDefault = 8;
        cols.push(c_opt10);

        if (cacheStore.ClinicSettings.EnableCovidQuestionnaire) {
            var c_opt11 = new MyleTableColumnOptions();
            c_opt11.HeaderText = cacheStore.GetString("QuestionnaireLabel");
            c_opt11.FieldName = "Questionnaire";
            c_opt11.RowClickEnabled = defaultRowClickEnabled;
            c_opt11.WidthPercentageDefault = 8;
            cols.push(c_opt11);
        }

        var c_opt12 = new MyleTableColumnOptions();
        c_opt12.HeaderText = cacheStore.GetString("StatusLabel");
        c_opt12.FieldName = "AppointmentStateLookupName";
        c_opt12.RowClickEnabled = defaultRowClickEnabled;
        c_opt12.WidthPercentageDefault = 8;
        c_opt12.IsSortable = true;
        c_opt12.SortColumn = PAListSortOrderType.Status;
        c_opt12.SortDirection = SortDirection.ASC;
        c_opt12.IsFixedInPlace = true;
        cols.push(c_opt12);

        if (cacheStore.ClinicSettings?.EnablePortalInvitationFlowV2) {
            const c_opt14 = new MyleTableColumnOptions();
            c_opt14.HeaderText = cacheStore.GetString("PortalLabel");
            c_opt14.FieldName = "Portal";
            c_opt14.RowClickEnabled = defaultRowClickEnabled;
            c_opt14.WidthPercentageDefault = 4;
            c_opt14.IsFixedInPlace = true;
            cols.push(c_opt14);
        }

        var c_opt13 = new MyleTableColumnOptions();
        c_opt13.HeaderText = cacheStore.GetString("PrintingOptionLabel");
        c_opt13.FieldName = "PrintOptions";
        c_opt13.RowClickEnabled = defaultRowClickEnabled;
        c_opt13.WidthPercentageDefault = 8;
        c_opt13.IsFixedInPlace = true;
        cols.push(c_opt13);

        return cols;
    }
}

export { PAListStore, defaultInstance as paListStore };
